import { useMemo } from 'react';

import { useFeedbackConfigs } from '../useSwr';
import { SESSION_FEEDBACK_PREFIX } from './constants';
import { FeedbackRow } from './types';
import { extractFeedbackKeys, getConfigMinMax, getFeedbackAvg } from './utils';

export function useMinMaxFeedbackValues(rows: FeedbackRow[]) {
  const feedbackKeys = useMemo(() => extractFeedbackKeys(rows), [rows]);

  const uniqueFeedbackKeys = useMemo(
    () => [...new Set(feedbackKeys.map((x) => x.feedbackKey))],
    [feedbackKeys]
  );

  const { data: feedbackConfigs } = useFeedbackConfigs(
    { skip: feedbackKeys.length === 0 },
    { key: uniqueFeedbackKeys }
  );

  const minMaxFeedbackValues = useMemo(() => {
    const resultMap = new Map<string, [number, number]>();

    for (const { feedbackKey: type, rowIdx } of feedbackKeys) {
      const isSessionFeedback = type.startsWith(SESSION_FEEDBACK_PREFIX);
      const lookupType = isSessionFeedback
        ? type.replace(SESSION_FEEDBACK_PREFIX, '')
        : type;

      const config = feedbackConfigs?.find(
        (i) => i.feedback_key === lookupType
      )?.feedback_config;

      const [configMin, configMax] = getConfigMinMax(config);
      const avg = getFeedbackAvg(rows, rowIdx, type, lookupType);

      if (avg == null) continue;

      const [currentMin, currentMax] = resultMap.get(type) ?? [
        configMin,
        configMax,
      ];
      resultMap.set(type, [
        Math.min(configMin, currentMin, avg),
        Math.max(configMax, currentMax, avg),
      ]);
    }

    return resultMap;
  }, [rows, feedbackConfigs, feedbackKeys]);

  return minMaxFeedbackValues;
}
